import type { PlatformProvider } from '@/api/schemasPlatformProviders';
import { FormField, fieldA11y } from '@/components/FormField';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PROVIDER_COPY, actionLabel } from './platformProvidersModel';
import { WhereToGetIt } from './WhereToGetIt';
import { usePlatformTokenForm, type PlatformTokenForm } from './usePlatformTokenForm';

interface Props {
  /** The provider to set; null closes the sheet. */
  provider: PlatformProvider | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Set or replace one of Malible's provider tokens (SPEC decision 19). A password field: the token is checked with the
 * provider by the API, stored in AWS Secrets Manager and never shown again; the form unmounts when the sheet closes.
 */
export function PlatformTokenSheet({ provider, onOpenChange }: Props) {
  return (
    <Sheet open={Boolean(provider)} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="platform-token-sheet" className="w-full gap-0 p-0 sm:max-w-md" aria-describedby="platform-token-desc">
        {provider ? <TokenForm key={provider.provider} current={provider} onDone={() => onOpenChange(false)} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function TokenForm({ current, onDone }: { current: PlatformProvider; onDone: () => void }) {
  const f = usePlatformTokenForm(current, onDone);
  const copy = PROVIDER_COPY[f.kind];
  return (
    <form data-testid="platform-token-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex h-full min-h-0 flex-col" aria-label={`${actionLabel(current)} for ${copy.name}`}>
      <SheetHeader>
        <SheetTitle>{actionLabel(current)} · {copy.name}</SheetTitle>
        <SheetDescription id="platform-token-desc" className="text-xs">{copy.purpose}</SheetDescription>
      </SheetHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4 sm:p-5">
        <WhereToGetIt kind={f.kind} />
        <TokenFields f={f} />
        <ProblemAlert error={f.problem} testId="platform-token-problem" />
      </div>
      <SheetFooter className="flex-row items-center justify-end gap-2 border-t">
        <Button variant="ghost" onClick={onDone}>Cancel</Button>
        <Button type="submit" data-testid="platform-token-submit" loading={f.busy}>
          {f.busy ? 'Checking…' : 'Check and save'}
        </Button>
      </SheetFooter>
    </form>
  );
}

function TokenFields({ f }: { f: PlatformTokenForm }) {
  const copy = PROVIDER_COPY[f.kind];
  return (
    <>
      <FormField id="platform-token-value" label={copy.tokenLabel} required hint={copy.tokenHint} error={f.errors.token}>
        <Input
          data-testid="platform-token-value" type="password" value={f.token} onChange={(e) => f.edit('token', e.target.value)}
          autoComplete="off" spellCheck={false} autoFocus className="font-mono sm:text-[13px]" {...fieldA11y('platform-token-value', f.errors.token, 'hint')}
        />
      </FormField>
      {f.kind === 'bitrise' ? (
        <FormField id="platform-token-workspace" label="Workspace slug" required hint="Workspace settings → General settings shows it, e.g. a1b2c3d4e5f6" error={f.errors.workspace}>
          <Input
            data-testid="platform-token-workspace" value={f.workspace} onChange={(e) => f.edit('workspace', e.target.value)}
            autoComplete="off" spellCheck={false} className="font-mono sm:text-[13px]" {...fieldA11y('platform-token-workspace', f.errors.workspace, 'hint')}
          />
        </FormField>
      ) : null}
    </>
  );
}
